import { PaymentType } from "src/api";
import { useDepositWithdrawContext } from "src/contexts";
import { formatNumberToLocale } from "src/utils";
import { _t } from "src/utils/translation";

interface UsePaymentValidationProps {
  amount: number;
  paymentType: PaymentType;
  fee?: number;
  /**
   * Tightest remaining headroom across the user's limit-group periods
   * (in wallet currency), from `useCalculatedLimits`. `Infinity`/undefined
   * means no cap configured — no limit error is raised.
   */
  availableLimit?: number;
}

export const usePaymentValidation = ({
  amount,
  paymentType,
  fee = 0,
  availableLimit = Infinity,
}: UsePaymentValidationProps) => {
  const { paymentsLimits, balance } = useDepositWithdrawContext();
  const {
    currency: { currency_id, precision },
    free_balance,
  } = balance;

  const minAmount =
    paymentType === PaymentType.Deposit ? paymentsLimits.deposit_min_amount : paymentsLimits.withdrawal_min_amount;
  const minAmountError = amount && amount < minAmount;

  const notEnoughBalance = paymentType === PaymentType.Withdrawal && amount && free_balance < amount;

  const amountAfterFeeError = amount && fee && amount - fee <= 0;

  // Block when the amount exceeds the user's remaining limit-group headroom.
  const limitExceeded = Number.isFinite(availableLimit) && amount && amount > availableLimit;

  const paymentValidationErrors = [
    limitExceeded
      ? [
          _t("VALIDATION.LIMIT_EXCEEDED", {
            defaultValue: "Amount exceeds your available {{paymentType}} limit ({{available}} {{currency}})",
            paymentType,
            available: formatNumberToLocale(availableLimit, precision),
            currency: currency_id,
          }),
        ]
      : [],
    minAmountError
      ? [
          _t("VALIDATION.MINIMUM_AMOUNT", {
            paymentType,
            minAmount,
            currency: currency_id,
          }),
        ]
      : [],
    notEnoughBalance
      ? [
          _t("VALIDATION.NOT_ENOUGH_BALANCE", {
            balance: free_balance,
          }),
        ]
      : [],
    amountAfterFeeError
      ? [
          _t("VALIDATION.AMOUNT_AFTER_FEE_SHOULD_BE_GREATER_THAN_ZERO"),
        ]
      : [],
  ].flat();

  return {
    paymentValidationErrors,
  };
};
